import { useCallback, useEffect, useState } from "react";

import {
  defaultOrgDashboardLayout,
  orgDashboardLayoutEvent,
  orgDashboardLayoutKey,
} from "../../constants/org-dashboard";
import { DashboardLayout } from "../../types/org-dashboard";
import { parseStoredLayout } from "../org-dashboard-layout";

/**
 * Per-user dashboard layout for the organisation page (FG-8053).
 *
 * Stored per user rather than per organisation: someone in support wants the
 * same arrangement on every org they open. Follows the useNavRedesign pattern —
 * localStorage plus a CustomEvent so every consumer updates without a reload.
 */
const readLayout = (knownCards: string[]): DashboardLayout =>
  parseStoredLayout(localStorage.getItem(orgDashboardLayoutKey), knownCards) ??
  defaultOrgDashboardLayout;

export const saveOrgDashboardLayout = (layout: DashboardLayout): void => {
  localStorage.setItem(orgDashboardLayoutKey, JSON.stringify(layout));
  window.dispatchEvent(
    new CustomEvent(orgDashboardLayoutEvent, { detail: { layout } }),
  );
};

export const clearOrgDashboardLayout = (): void => {
  localStorage.removeItem(orgDashboardLayoutKey);
  window.dispatchEvent(new CustomEvent(orgDashboardLayoutEvent));
};

interface UseOrgDashboardLayout {
  layout: DashboardLayout;
  setLayout: (layout: DashboardLayout) => void;
  resetLayout: () => void;
  isCustomised: boolean;
}

const useOrgDashboardLayout = (knownCards: string[]): UseOrgDashboardLayout => {
  const [layout, setLayoutState] = useState<DashboardLayout>(() =>
    readLayout(knownCards),
  );
  const [isCustomised, setIsCustomised] = useState<boolean>(
    () => localStorage.getItem(orgDashboardLayoutKey) !== null,
  );

  useEffect(() => {
    const handleChange = (event: Event) => {
      const detail = (event as CustomEvent<{ layout?: DashboardLayout }>)
        .detail;
      setLayoutState(detail?.layout ?? readLayout(knownCards));
      setIsCustomised(localStorage.getItem(orgDashboardLayoutKey) !== null);
    };
    window.addEventListener(orgDashboardLayoutEvent, handleChange);
    return () => {
      window.removeEventListener(orgDashboardLayoutEvent, handleChange);
    };
    // knownCards is a stable module-level list; re-subscribing on it would
    // needlessly tear the listener down on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLayout = useCallback((next: DashboardLayout) => {
    saveOrgDashboardLayout(next);
  }, []);

  const resetLayout = useCallback(() => {
    clearOrgDashboardLayout();
  }, []);

  return { layout, setLayout, resetLayout, isCustomised };
};

export default useOrgDashboardLayout;
